import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFontSize } from "../../../redux/slices/fontSettingsSlice";
import { RootState } from "../../../redux/store";

const FONT_SIZE_LABELS: Record<number, string> = {
  0: "Extra Small",
  1: "Small",
  2: "Default",
  3: "Large",
  4: "Extra Large",
} as const;

const FontSize = () => {
  const dispatch = useDispatch();
  const fontSize = useSelector(
    (state: RootState) => state.fontSettings.fontSize
  );

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = Number(e.target.value);
    dispatch(setFontSize(newSize));
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newValue = Math.round(percentage * 4);
    const clampedValue = Math.max(0, Math.min(4, newValue));

    dispatch(setFontSize(clampedValue));
  };

  return (
    <div className="flex flex-col gap-[24px] px-[24px] py-[16px]">
      <p className="text-base font-normal text-data dark:text-data-dk">
        Customise your dashboard by changing the font size
      </p>

      <div className="flex items-center gap-[10px]">
        <p className="text-[18px] font-normal text-verba-indigo dark:text-verba-indigo-dk">
          A
        </p>
        <div className="relative w-full pt-2 pb-4">
          <div className="relative h-[4px] rounded-full">
            <div
              className="absolute w-full h-full bg-border-gray dark:bg-border-gray-dk rounded-full cursor-pointer"
              onClick={handleTrackClick}
            />

            <div className="absolute w-full h-full flex justify-between pointer-events-none">
              {[0, 1, 2, 3, 4].map((tick) => (
                <div key={tick} className="relative">
                  <div
                    className="absolute h-[10px] rounded-full w-[3px] bg-border-gray dark:bg-border-gray-dk"
                    style={{
                      left: tick === 0 ? "6px" : tick === 4 ? "-9px" : "-1px",
                      top: "-3px",
                    }}
                  />
                  <div
                    className={`absolute text-[12px] text-verba-indigo dark:text-verba-indigo-dk font-normal ${
                      fontSize !== tick && "hidden"
                    }`}
                    style={{
                      left:
                        tick === 0 ? "-12px" : tick === 4 ? "-24px" : "-16px",
                      top: "18px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {FONT_SIZE_LABELS[tick]}
                  </div>
                </div>
              ))}
            </div>

            <input
              type="range"
              min="0"
              max="4"
              value={fontSize}
              onChange={handleSliderChange}
              step="1"
              className="absolute w-full h-[20px] opacity-0 cursor-pointer top-[-8px]"
              style={{ touchAction: "none" }}
            />

            <div
              className="absolute h-full bg-border-gray dark:bg-border-gray-dk rounded-full pointer-events-none"
              style={{
                width: `${(fontSize / 4) * 100}%`,
                transition: "width 0.2s ease-in-out",
              }}
            />

            <div
              className="absolute h-4 w-4 bg-verba-blue-dark-mode dark:bg-verba-blue-dark-mode-dk rounded-full -translate-y-1.5 shadow-slider-thumb pointer-events-none"
              style={{
                left: `calc(${(fontSize / 4) * 100}% - 8px)`,
                transition: "left 0.2s ease-in-out",
              }}
            />
          </div>
        </div>
        <p className="text-[33px] font-normal text-verba-indigo dark:text-verba-indigo-dk">
          A
        </p>
      </div>

      <hr className="border-border-gray dark:border-border-gray-dk" />

      <div>
        <h3 className="text-base font-semibold text-data dark:text-data">
          This is a Preview
        </h3>
        <p className="text-md font-normal text-data dark:text-data-dk">
          VerbaFlo is the world's most adaptable conversational AI
        </p>
      </div>
    </div>
  );
};

export default FontSize;
